import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { PostRetryService } from '@app/services/post-retry/post-retry.service';
import { v4 as uuid } from 'uuid';
import { Subject } from 'rxjs';
import { InputFormComponent } from '@app/components/shared/input-form/input-form.component';
import { RiderEventType } from '@app/constants/rider-event-type';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatButton } from '@angular/material/button';
import { PageLayoutComponent } from '@app/components/shared/page-layout/page-layout.component';
import { PageTitleComponent } from '@app/components/common/page-title/page-title.component';

import { RiderFacadeService } from '@app/facade-services/rider/riders-facade.service';
import { Rider } from '@app/rest-client/faxe/models/rider';
import { filter, takeUntil } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { ClubbLoggoService } from '@app/services/clubb-loggo.service';
import { RiderEventPost } from '@app/rest-client/faxe/models/rider-event-post';
import { SliceState } from '@app/store/faxe/sapiens-entity-state';
import { PasswordService } from '@app/services/password/password.service';
import { UserToken } from '@app/services/password/model/user-token';
import { ListItem } from '@app/components/shared/rolling-hist-list/model/list-item';
import { RollingHistListService } from '@app/components/shared/rolling-hist-list/service/rolling-hist-list.service';
import { RollingHistListComponent } from '@app/components/shared/rolling-hist-list/rolling-hist-list.component';

@Component({
  selector: 'faxe-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    InputFormComponent,
    MatListModule,
    MatIconModule,
    MatButton,
    PageLayoutComponent,
    PageTitleComponent,
    RollingHistListComponent,
  ],
})
export class StartComponent implements OnInit, OnDestroy {
  @ViewChild('inputstart') inputComponent: InputFormComponent;
  formGroup: UntypedFormGroup = this.formBuilder.group({});
  listItems: ListItem<any>[] = [];
  riders: Rider[] = [];
  startPending = false;
  userToken: UserToken;
  histListBucketId = 'start';
  private unsubscribe$ = new Subject<void>();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly riderFacadeService: RiderFacadeService,
    private readonly clubbLoggoService: ClubbLoggoService,
    private readonly passwordService: PasswordService,
    private readonly rollingHistListService: RollingHistListService,
    private readonly postRetryService: PostRetryService
  ) {}

  ngOnInit(): void {
    this.passwordService
      .raceUserToken()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((token) => {
        this.userToken = token;
        this.riderFacadeService.fetchRiders(
          token.selectedRaceGuid,
          'StartComponent',
          SliceState.total
        );
        this.riderFacadeService
          .selectRiderByRaceGuid(token.selectedRaceGuid, SliceState.total)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((riders) => {
            this.riders = riders;
          });
      });

    this.rollingHistListService.histListMap$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((h) => h?.has(this.histListBucketId))
      )
      .subscribe((histListMap) => {
        this.listItems = histListMap.get(this.histListBucketId);
      });
  }

  private logoSrc(clubName: string): string {
    return this.clubbLoggoService.logoSrc(clubName);
  }

  horseCombinationNumber = (): number =>
    this.formGroup?.value?.start?.value
      ? +this.formGroup?.value?.start?.value
      : undefined;

  riderEventPost = (eventType: string, date: Date): RiderEventPost => ({
    eventTimestamp: date?.toISOString(),
    eventType,
    raceGuid: this.userToken.selectedRaceGuid,
  });

  onStart() {
    this.formGroup?.updateValueAndValidity();
    if (this.formGroup?.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.startPending = true;
    const horseCombinationNumber: number = this.horseCombinationNumber();
    const start: Date = new Date();
    const riderEvent = this.riderEventPost(RiderEventType.started, start);

    const itemId = uuid();
    this.rollingHistListService.unshift(this.histListBucketId, {
      id: itemId,
      key: horseCombinationNumber,
      heading: `Ryttarnummer ${horseCombinationNumber}`,
      h2: `Startad: ${start.toLocaleTimeString()}`,
      h3: this.riders?.find(
        (rider) => rider.horseCombinationNumber === horseCombinationNumber
      )?.riderName,
      logoSrc: this.logoSrc(
        this.riders?.find(
          (rider) => rider.horseCombinationNumber === horseCombinationNumber
        )?.clubName
      ),
    });

    this.postRetryService.enqueue(
      'riderEvent',
      {
        raceGuid: this.userToken.selectedRaceGuid,
        horseCombinationNumber,
        body: riderEvent,
      },
      this.histListBucketId,
      itemId
    );

    this.startPending = false;
    this.inputComponent?.setFocus();
    this.inputComponent?.clear();
    this.formGroup?.markAsUntouched();
  }

  onInput() {
    this.startPending = false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
